import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CaretLeft, PaperPlaneTilt, WarningCircle } from '@phosphor-icons/react';

export default function ChatPage() {
  const { jobId, userId } = useParams();
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [phoneWarn, setPhoneWarn] = useState(false);
  const bottomRef = useRef(null);

  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/chat/messages/${jobId}/${userId}`);
      setMessages(data.messages || []);
    } catch {}
  };

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, [jobId, userId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    setSending(true);
    setPhoneWarn(false);
    try {
      await api.post('/chat/send', { job_id: jobId, recipient_id: userId, message: input.trim() });
      setInput('');
      loadMessages();
    } catch (err) {
      if (err.response?.data?.detail?.includes('Phone')) setPhoneWarn(true);
    } finally { setSending(false); }
  };

  return (
    <div className="h-screen bg-[#E8DDD2] flex flex-col max-w-[480px] mx-auto" data-testid="chat-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-black/5 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-bold text-[#1C1C1C] truncate">Job Chat</div>
          <div className="text-[10px] text-[#5C5C5C]">Secure in-app messaging</div>
        </div>
        <div className="w-9 h-9 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white text-xs font-bold">?</div>
      </header>

      {phoneWarn && (
        <div className="bg-red-50 border-b-2 border-red-400 px-4 py-2 text-xs text-red-600 font-bold flex items-center gap-2 flex-shrink-0" data-testid="phone-warning">
          <WarningCircle size={16} /> Phone numbers are not allowed in chat
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
        {messages.length === 0 && (
          <div className="text-center py-16">
            <p className="text-sm text-[#5C5C5C] font-body">No messages yet. Start the conversation!</p>
          </div>
        )}
        {messages.map(m => {
          const isMine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}>
              {!isMine && <span className="text-[10px] text-[#5C5C5C] font-bold ml-1 mb-0.5">{m.sender_name}</span>}
              <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm font-body ${isMine ? 'bg-[#E86A33] text-white rounded-br-sm' : 'bg-white border border-[#D4C5B3] text-[#1C1C1C] rounded-bl-sm'}`} data-testid={`message-${m.id}`}>
                {m.message}
                <span className={`block text-[10px] mt-1 ${isMine ? 'text-white/60 text-right' : 'text-[#5C5C5C]'}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="bg-white/80 backdrop-blur-xl border-t border-black/5 px-3 py-3 flex items-end gap-2 flex-shrink-0">
        <div className="flex-1 bg-[#E8DDD2] border border-[#D4C5B3] rounded-2xl px-4 py-2.5 focus-within:border-[#E86A33]">
          <textarea data-testid="chat-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
            placeholder="Type a message..." rows={1} className="w-full bg-transparent text-sm text-[#1C1C1C] font-body outline-none resize-none placeholder:text-[#5C5C5C]/50" />
        </div>
        <button onClick={sendMessage} disabled={sending || !input.trim()} className="w-11 h-11 bg-[#E86A33] rounded-full flex items-center justify-center text-white flex-shrink-0 disabled:opacity-40 hover:bg-[#D35A29] transition-all" data-testid="send-message-button">
          <PaperPlaneTilt size={20} weight="fill" />
        </button>
      </div>
    </div>
  );
}
